// Rail kinds: what a carousel is for (highlights, replays, a podcast, and so
// on). The widget type says how big the cards are; the rail kind is what
// dynamic carousel ordering ranks, when it is on.
//
// Keep in sync with:
//   apps/ace/internal/railorder/railorder.go   (the kinds and their tiers)
//   apps/ace/internal/railorder/infer.go       (the same suggestion rules)
//   apps/api/shared/json_schemas/tc.widget.card_carousel.v1.json (rail_kind enum)
//
// Nothing keeps the three in sync, so adding a kind takes three edits.

export interface RailKindOption {
  value: RailKind
  label: string
  /** Shown under the picker to tell similar kinds apart. */
  hint: string
}

export type RailKind =
  | 'live_and_upcoming'
  | 'live_show'
  | 'match_in_15'
  | 'exclusive_interviews'
  | 'press_conferences'
  | 'podcast_full_episodes'
  | 'podcast_clips'
  | 'replays'
  | 'highlights'
  | 'vod_clips'
  | 'series_and_originals'

// Listed by the best tier each kind can reach, highest first.
export const RAIL_KIND_OPTIONS: RailKindOption[] = [
  { value: 'live_and_upcoming', label: 'Live & Upcoming', hint: 'Leads the page while anything is live or starts within the hour.' },
  { value: 'live_show', label: 'Live Show', hint: 'A channel that is on air.' },
  { value: 'match_in_15', label: 'Match in 15', hint: 'Condensed matches. Ranks on how fresh the newest one is.' },
  { value: 'exclusive_interviews', label: 'Exclusive Interviews', hint: '' },
  { value: 'press_conferences', label: 'Press Conferences', hint: '' },
  { value: 'podcast_full_episodes', label: 'Podcast – Full Episodes', hint: '' },
  { value: 'podcast_clips', label: 'Podcast – Clips', hint: '' },
  { value: 'replays', label: 'Replays', hint: '' },
  { value: 'highlights', label: 'Highlights', hint: '' },
  { value: 'vod_clips', label: 'VOD Clips', hint: '' },
  { value: 'series_and_originals', label: 'Series & Originals', hint: 'Evergreen. Sits at the foot of the page.' },
]

export const RAIL_KIND_LABELS: Record<string, string> = Object.fromEntries(
  RAIL_KIND_OPTIONS.map(o => [o.value, o.label]),
)

/** The merchandising tag that marks podcast videos. It decides the kind of an
 * EXTRA or SERIES_EPISODE video, which the tier table ranks differently for
 * podcast and non-podcast content. */
const PODCAST_TAG = 'podcast'

const VOD_TYPE_KINDS: Record<string, RailKind> = {
  CONDENSED: 'match_in_15',
  INTERVIEW: 'exclusive_interviews',
  PRESS_CONF: 'press_conferences',
  HIGHLIGHT: 'highlights',
  REPLAY: 'replays',
  ORIGINAL: 'series_and_originals',
}

const PODCAST_SPLIT: Record<string, { withTag: RailKind; withoutTag: RailKind }> = {
  EXTRA: { withTag: 'podcast_clips', withoutTag: 'vod_clips' },
  SERIES_EPISODE: { withTag: 'podcast_full_episodes', withoutTag: 'series_and_originals' },
}

/** The metadata fields the suggestion reads. Loosely typed, because it is
 * given whatever the editor currently has on screen. */
interface InferenceInput {
  sources?: string[]
  filter_groups?: Array<Record<string, unknown>>
  filters?: Record<string, unknown>
}

/** Collect one filter key across every filter group, without duplicates.
 * Removing duplicates matters: two groups both asking for highlights list the
 * type twice, and counting it twice would look like a mixed rail and suggest
 * nothing. */
function gather(input: InferenceInput, keys: string[]): string[] {
  const groups = input.filter_groups?.length
    ? input.filter_groups
    : (input.filters ? [input.filters] : [])
  const out = new Set<string>()
  for (const group of groups) {
    for (const key of keys) {
      const value = group?.[key]
      if (Array.isArray(value)) {
        for (const entry of value) {
          if (typeof entry === 'string' && entry.trim()) out.add(entry.trim())
        }
      }
    }
  }
  return [...out]
}

/**
 * Suggest a rail kind from the filters an editor has set, or return null.
 *
 * Returns null when no single kind fits: zero or several sources or VOD types,
 * a match rail with no lifecycle filter, or an unknown source or VOD type. An
 * unlabelled rail is ranked on what it returns.
 *
 * Only a suggestion: nothing here writes metadata. The editor clicks
 * "Use it" to accept it.
 */
export function inferRailKind(input: InferenceInput): RailKind | null {
  const sources = [...new Set((input.sources ?? []).map(s => s.trim().toLowerCase()).filter(Boolean))]
  if (sources.length !== 1) return null

  if (sources[0] === 'channels') return 'live_show'

  if (sources[0] === 'matches') {
    const lifecycle = gather(input, ['lifecycleState', 'lifecycle_state']).map(s => s.toUpperCase())
    if (lifecycle.length === 0) return null
    if (lifecycle.includes('LIVE') || lifecycle.includes('UPCOMING')) return 'live_and_upcoming'
    return 'replays'
  }

  if (sources[0] === 'vod') {
    const vodTypes = gather(input, ['vod_types', 'vodTypes']).map(s => s.toUpperCase())
    if (vodTypes.length !== 1) return null
    const tags = gather(input, ['tags']).map(s => s.toLowerCase())
    const split = PODCAST_SPLIT[vodTypes[0]]
    if (split) return tags.includes(PODCAST_TAG) ? split.withTag : split.withoutTag
    return VOD_TYPE_KINDS[vodTypes[0]] ?? null
  }

  return null
}
